import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { FiUserPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../../store';
import { deleteTemplate } from '../../../store/slices/whatsappTemplateSlice';
import { useLeadStages } from '../../../hooks/useLeadStages';
import PageWrapper from '../../common/PageWrapper';
import CreateWhatsAppTemplateModal from '../../modules/WhatsApp/CreateWhatsAppTemplateModal';
import type { WhatsAppTemplate } from '../../../types/whatsappTemplate';
import './WhatsAppTemplatePage.css';

const WhatsAppTemplatePage = () => {
  const dispatch = useAppDispatch();
  const { templates } = useAppSelector((state) => state.whatsappTemplates);
  const { getStageLabel } = useLeadStages();

  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAddNew = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: WhatsAppTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      dispatch(deleteTemplate(id));
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  // const getStageLabel = (stage: string) => {
  //   return STEPPER_STAGES.find((s) => s.key === stage)?.displayLabel || stage;
  // };

  const tableColumns = ['Stage', 'Title', 'Message Preview', 'Actions'];

  return (
    <PageWrapper>
      <div className="whatsapp-template-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <div className="page-title-section">
              <span className="title-icon">
                <FaWhatsapp style={{ color: '#25D366' }} />
              </span>
              <h1 className="page-title">WhatsApp Message Templates</h1>
            </div>
            <p className="page-description">
              Create and manage predefined WhatsApp messages for each stage.
            </p>
          </div>

          <button
            type="button"
            className="add-new-btn"
            onClick={handleAddNew}
          >
            <FiUserPlus className="btn-icon" />
            Create Message
          </button>
        </div>

        {/* Template List */}
        <div className="min-w-0">
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {tableColumns.map((column) => (
                    <TableCell key={column} sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No templates found. Click "Create Message" to add a new template.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow
                      key={template.id}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#f8fafc' },
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(template)}
                    >
                      <TableCell>
                        <Chip
                          label={getStageLabel(template.stage)}
                          size="small"
                          sx={{
                            bgcolor: '#e0f2fe',
                            color: '#0369a1',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {template.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={template.message}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#1e293b',
                                color: '#ffffff',
                                fontSize: '0.875rem',
                                padding: '0.75rem 1rem',
                                maxWidth: 500,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#1e293b',
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 400,
                              //   cursor: 'help',
                            }}
                          >
                            {template.message}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(template)}
                          sx={{ color: '#3b82f6' }}
                        >
                          <FiEdit2 />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(template.id)}
                          sx={{ color: '#ef4444' }}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Sidebar Modal */}
      <CreateWhatsAppTemplateModal
        isOpen={isModalOpen}
        onRequestClose={handleModalClose}
        editingTemplate={editingTemplate}
      />
    </PageWrapper>
  );
};

export default WhatsAppTemplatePage;

